import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Multimedia } from 'src/app/modules/shared/models/multimedia';
import { Instructor } from '../../models/instructor';
import { Feedback } from '../../models/feedback';
import { InstructorReviewFilter, Review, ReviewReq } from '../../models/review';
import { CourseTemplate } from '../../models/course-template';
import { ConfigService } from '@core/services/config.service';
import { BaseApiService } from '@core/http/base.api.service';
import { PageList } from '@core/models/page-list';
import { InstructorFilter } from "@modules/instructor/models/instructor-filter";
import { InstructorPagination } from "@modules/instructor/models/instructor-pagination";
import { InstructorIncludeType } from "@modules/instructor/enums/instructor-include-type";

@Injectable({
  providedIn: 'root'
})
export class InstructorApiService extends BaseApiService {
  constructor(
    public http: HttpClient,
    private configService: ConfigService
  ) {
    super(http);
  }

  private get baseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.classroom}/api/instructor`;
  }

  private get courseTemplateBaseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.classroom}/api/template`;
  }

  public getAllInstructors(instructorFilters: InstructorFilter,
                           instructorPagination: InstructorPagination): Observable<PageList<Instructor>> {
    const params = new HttpParams()
      .append('search', instructorFilters.search)
      .append('regions', instructorFilters.regions)
      .append('templates', instructorFilters.templates)
      .append('categories', instructorFilters.categories)
      .append('onlyAccepted', instructorFilters.includeApprove)
      .append('onlyRequested', instructorFilters.includePending)
      .append('onlyBlocked', instructorFilters.includeBlocked)
      .append('onlyRejected', instructorFilters.includeRejected)
      .append('onlyDrafted', instructorFilters.includeDraft)
      .append('onlyReturned', instructorFilters.includeReturned)
      .append('pageOffset', instructorPagination.pageIndex.toString())
      .append('pageSize', instructorPagination.pageSize.toString())
      .append('sort', instructorFilters.sort);

    return super.getAllWithPagination<Instructor>(this.baseUrl, params);
  }

  public fetchInstructorById(id: string, includes: InstructorIncludeType[] = null): Observable<Instructor> {
    const params = new HttpParams().appendAll({ 'infoType': includes });

    return this.http.get<Instructor>(`${this.baseUrl}/${id}`, { params: params });
  }

  public registerInstructor(instructor: Partial<Instructor>): Observable<Instructor> {
    return this.http.post<Instructor>(`${this.baseUrl}`, instructor);
  }

  public updateCourseTemplate(
    instructorId: string,
    courseTemplates: { courseTemplateId: string }[]
  ): Observable<CourseTemplate> {
    return this.http.post<CourseTemplate>(`${this.baseUrl}/${instructorId}/templates`, courseTemplates);
  }

  public updateInstructor(id: string, instructor: Partial<Instructor>) {
    return this.http.put<Instructor>(`${this.baseUrl}/${id}`, instructor);
  }

  public getOwnInfo(): Observable<Instructor> {
    return this.http.get<Instructor>(`${this.baseUrl}/me`);
  }

  public uploadInstructorMedia(
    id: string,
    file: File,
    tags: string,
    category: string,
    defualt: boolean = true
  ): Observable<Multimedia[]> {
    const params = new HttpParams()
      .append('tags', tags)
      .append('category', category)
      .append('default', defualt ? 'true' : 'false');

    const formData = new FormData();
    formData.append('files', file);

    return this.http.post<Multimedia[]>(`${this.baseUrl}/${id}/media`, formData, { params: params });
  }

  public deleteMedia(id: string, fileName: string): Observable<any> {
    const params = new HttpParams().append('name', fileName);

    return this.http.delete(`${this.baseUrl}/${id}/media`, { params: params });
  }

  public getInstructorReviews(instructorId: string, filter: InstructorReviewFilter): Observable<PageList<Review>> {
    const params = new HttpParams().appendAll({...filter});
    return super.getAllWithPagination<Review>(`${this.baseUrl}/${instructorId}/review`, params);
  }

  public getInstructorFeedbacksById(instructorId: string): Observable<Feedback[]> {
    return this.http.get<Feedback[]>(`${this.baseUrl}/${instructorId}/feedbacks`);
  }

  fetchCourseTemplate(): Observable<CourseTemplate[]> {
    return this.http.get<CourseTemplate[]>(this.courseTemplateBaseUrl);
  }

  sendInstructorReview(id: string, review: ReviewReq): Observable<Review> {
    return this.http.post<Review>(`${this.baseUrl}/${id}/review`, review);
  }

  public setNotificationPreference(
    instructorId: string = '',
    notificationPreference: boolean = true
  ): Observable<any> {
    const  param = new HttpParams().set('notificationPreference', notificationPreference);
    return this.http.put<any>(`${this.baseUrl}/${instructorId}/notificationPreference`, null, { params: param });
  }

  public getNotificationPreference(
    instructorId: string = ''
  ): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/${instructorId}/notificationPreference`);
  }
}
